"use client";

import {
  EmailScannerTagger,
  parsePosthogEmailScannerTagEnabled,
} from "@/lib/analytics/bot-traffic";
import { dropKnownExceptionNoise } from "@/lib/analytics/posthog-exception-noise";
import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { useEffect, useRef } from "react";

// One-time reset for browsers that accumulated a person profile under the
// previous `person_profiles: "always"` config, so old profiles stop gaining
// new events (student-privacy hardening, PLA-419).
function resetExistingPersonProfileOnce(): void {
  try {
    const RESET_FLAG = "playmath.phProfileResetV1";
    if (typeof window !== "undefined" && !window.localStorage.getItem(RESET_FLAG)) {
      posthog.reset();
      window.localStorage.setItem(RESET_FLAG, "1");
    }
  } catch {
    // Non-critical
  }
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const isPosthogInitialized = useRef<boolean>(false);
  useEffect(() => {
    // Check if PostHog is already initialized (either by this component or elsewhere)
    if (isPosthogInitialized.current || (posthog as unknown as { __loaded?: boolean }).__loaded) {
      return;
    }
    isPosthogInitialized.current = true;

    const isDev = process.env.APP_ENV === "development" || process.env.APP_ENV === "test";
    const shouldForcePosthogInDev =
      process.env.FORCE_POSTHOG_IN_DEV?.length &&
      process.env.FORCE_POSTHOG_IN_DEV.toLowerCase() !== "false";
    const posthogApiUrl =
      isDev && !shouldForcePosthogInDev
        ? "/ingest"
        : process.env.NEXT_PUBLIC_POSTHOG_HOST || "/ingest";

    const emailScannerTagger = new EmailScannerTagger(
      parsePosthogEmailScannerTagEnabled(process.env.NEXT_PUBLIC_POSTHOG_EMAIL_SCANNER_TAG)
    );
    // Persist landing utm_medium before init so the first $pageview can match.
    emailScannerTagger.installSessionHooks();

    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
      api_host: posthogApiUrl,
      capture_dead_clicks: false,
      ui_host: "https://us.posthog.com",
      defaults: "2025-05-24",
      person_profiles: "never",
      capture_exceptions: true, // This enables capturing exceptions using Error Tracking, set to false if you don't want this
      // PLA-796: tag email link sandboxes; PLA-701/702: drop exception noise.
      before_send: [emailScannerTagger.beforeSend, dropKnownExceptionNoise],
      debug: process.env.APP_ENV === "development",
      session_recording: {
        // Pairs with /scripts/game-replay/v1 inside embedded web games (docs/GAME-SESSION-REPLAY.md).
        recordCrossOriginIframes: true,
      },
    });

    resetExistingPersonProfileOnce();
    if (posthogApiUrl === "/ingest") {
      console.log(
        "Running posthog locally. If you want to run the live check FORCE_POSTHOG_IN_DEV and APP_ENV are set accordingly."
      );
    }
  }, []);

  return <PHProvider client={posthog}>{children}</PHProvider>;
}
